/**
 * Observable diagnostics for the native push-registration flow.
 *
 * Every step of `usePushNotifications` reports here so the Profile page can
 * surface EXACTLY where push registration breaks on-device (no AWS access
 * required). Module-level singleton + useSyncExternalStore subscribers.
 */

export type PushBackendStatus = 'idle' | 'pending' | 'success' | 'error'

export type PushDiagnosticsState = {
  /** capacitor platform string ('android' | 'ios' | 'web') */
  platform: string
  /** running inside a Capacitor native shell */
  isNativeApp: boolean
  /** VITE_ENABLE_PUSH === 'true' at build time */
  enablePushFlag: boolean
  /** google-services.json / GoogleService-Info.plist present at build time */
  nativePushCompiled: boolean
  /** combined isNativePushEnabled() result the hook actually gates on */
  isNativePushEnabled: boolean
  /** last observed OS permission state (checkPermissions().receive) */
  permission: string
  /** PushNotifications.register() has been invoked at least once */
  registerCalled: boolean
  /** FCM/APNs device token from the registration event, if received */
  token: string | null
  /** last FCM/APNs registrationError payload, if any */
  registrationError: string | null
  /** state of the backend registerPushToken mutation */
  backendStatus: PushBackendStatus
  /** last backend error string, if any */
  backendError: string | null
  /** ISO timestamp of the last state change */
  updatedAt: string | null
  /** rolling event log (newest last), capped */
  log: string[]
}

const initialState: PushDiagnosticsState = {
  platform: 'unknown',
  isNativeApp: false,
  enablePushFlag: false,
  nativePushCompiled: false,
  isNativePushEnabled: false,
  permission: 'unknown',
  registerCalled: false,
  token: null,
  registrationError: null,
  backendStatus: 'idle',
  backendError: null,
  updatedAt: null,
  log: [],
}

let state: PushDiagnosticsState = { ...initialState }
const listeners = new Set<() => void>()

const MAX_LOG = 40

function emit() {
  for (const l of listeners) l()
}

function append(patch: Partial<PushDiagnosticsState>, logLine?: string) {
  const now = new Date()
  const log = logLine
    ? [...state.log, `${now.toLocaleTimeString()} ${logLine}`].slice(-MAX_LOG)
    : state.log
  state = { ...state, ...patch, log, updatedAt: now.toISOString() }
  emit()
}

export const pushDiagnostics = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },
  getSnapshot(): PushDiagnosticsState {
    return state
  },
  /** Record the build/runtime gating flags (called when the hook mounts). */
  setEnvironment(patch: {
    platform: string
    isNativeApp: boolean
    enablePushFlag: boolean
    nativePushCompiled: boolean
    isNativePushEnabled: boolean
  }) {
    append(patch, `env: native=${patch.isNativeApp} pushEnabled=${patch.isNativePushEnabled}`)
  },
  setPermission(permission: string) {
    append({ permission }, `permission: ${permission}`)
  },
  setRegisterCalled() {
    append({ registerCalled: true }, 'PushNotifications.register() called')
  },
  setToken(token: string) {
    append(
      { token, registrationError: null },
      `token received (${token.slice(0, 12)}…, len ${token.length})`,
    )
  },
  setRegistrationError(error: string) {
    append({ registrationError: error }, `registrationError: ${error}`)
  },
  setBackendStatus(status: PushBackendStatus, error?: string | null) {
    append(
      { backendStatus: status, backendError: error ?? (status === 'error' ? state.backendError : null) },
      `backend registerPushToken: ${status}${error ? ` — ${error}` : ''}`,
    )
  },
  note(line: string) {
    append({}, line)
  },
  reset() {
    state = { ...initialState }
    emit()
  },
}
